
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./TPMS_17.css";

const TITLE = "Business Benefits";

const BENEFITS = [
  {
    icon: "bi-graph-up-arrow",
    title: "Revenue leakage control",
    desc: "Helps detect suspicious or unrecorded transactions",
  },
  {
    icon: "bi-lightning-charge",
    title: "Fraud prevention",
    desc: "Creates independent evidence that discourages and exposes potential fraudulent activity",
  },
  {
    icon: "bi-eye",
    title: "Exempted vehicle control",
    desc: "Supports verification and auditing of exemption entries.",
  },
  {
    icon: "bi-bullseye",
    title: "Accurate vehicle monitoring",
    desc: "Reduces missed events and counting inconsistencies",
  },
  {
    icon: "bi-shield-check",
    title: "No additional roadside hardware",
    desc: "Uses compatible existing toll plaza cameras",
  },
  {
    icon: "bi-diagram-3",
    title: "Reduced manual supervision",
    desc: "Automates vehicle monitoring and evidence collection",
  },
  {
    icon: "bi-camera-video",
    title: "Better dispute resolution",
    desc: "Provides vehicle images and video evidence",
  },
  {
    icon: "bi-clipboard-check",
    title: "Stronger operational audits",
    desc: "Makes vehicle records easier to trace and verify",
  },
  {
    icon: "bi-grid-1x2",
    title: "Centralized visibility",
    desc: "Enables monitoring across multiple toll lanes",
  },
  {
    icon: "bi-speedometer2",
    title: "Improved operational efficiency",
    desc: "Streamlines vehicle analysis, reporting, and investigation",
  },
];

const Chevron = ({ dir }) => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path
      d={
        dir === "left"
          ? "m15 5-7 7 7 7"
          : "m9 5 7 7-7 7"
      }
    />
  </svg>
);

const TPMS_17 = () => {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const loopWidthRef = useRef(0);
  const currentRef = useRef(1);

  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(1);

  // ==========================================
  // SCROLL REVEAL ANIMATION
  // ==========================================

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // ==========================================
  // CALCULATE LOOP WIDTH
  // ==========================================

  const updateLoopWidth = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const cards = el.querySelectorAll(".tb__card");

    if (cards.length > BENEFITS.length) {
      loopWidthRef.current =
        cards[BENEFITS.length].offsetLeft -
        cards[0].offsetLeft;
    }
  }, []);

  // ==========================================
  // UPDATE COUNTER AND PROGRESS
  // ==========================================

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const loopWidth = loopWidthRef.current;
    if (!loopWidth) return;

    const left =
      ((el.scrollLeft % loopWidth) + loopWidth) %
      loopWidth;

    const cards = el.querySelectorAll(".tb__card");
    if (!cards.length) return;

    const firstCard = cards[0];
    const secondCard = cards[1];

    const step = secondCard
      ? secondCard.offsetLeft - firstCard.offsetLeft
      : firstCard.offsetWidth + 24;

    const activeIndex = Math.floor(left / step);

    const newCurrent =
      (activeIndex % BENEFITS.length) + 1;

    if (currentRef.current !== newCurrent) {
      currentRef.current = newCurrent;
      setCurrent(newCurrent);
    }

    setProgress(left / loopWidth);
  }, []);

  // ==========================================
  // INITIALIZE SLIDER
  // ==========================================

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const refresh = () => {
      updateLoopWidth();
      update();
    };

    refresh();

    const resizeObserver =
      "ResizeObserver" in window
        ? new ResizeObserver(refresh)
        : null;

    if (resizeObserver) {
      resizeObserver.observe(el);
    } else {
      window.addEventListener("resize", refresh);
    }

    el.addEventListener("scroll", update, {
      passive: true,
    });

    return () => {
      el.removeEventListener("scroll", update);

      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", refresh);
      }
    };
  }, [update, updateLoopWidth]);

  // ==========================================
  // CONTINUOUS INFINITE AUTO SLIDER
  // ==========================================

  useEffect(() => {
    const el = trackRef.current;

    if (!el || !isVisible) return;

    if (
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches
    ) {
      return;
    }

    let animationFrame;
    let previousTime = 0;

    // Speed in pixels per second
    const speed = 38;

    const animate = (time) => {
      if (!previousTime) {
        previousTime = time;
      }

      const delta = Math.min(
        (time - previousTime) / 1000,
        0.05
      );

      previousTime = time;

      const loopWidth = loopWidthRef.current;

      if (loopWidth > 0) {
        el.scrollLeft += speed * delta;

        // Seamless infinite repeat
        if (el.scrollLeft >= loopWidth) {
          el.scrollLeft -= loopWidth;
        }
      }

      animationFrame =
        requestAnimationFrame(animate);
    };

    animationFrame =
      requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [isVisible]);

  // ==========================================
  // PREVIOUS / NEXT BUTTONS
  // ==========================================

  const scrollByCards = (direction) => {
    const el = trackRef.current;
    if (!el) return;

    const cards = el.querySelectorAll(".tb__card");
    if (!cards.length) return;

    const step = cards[1]
      ? cards[1].offsetLeft - cards[0].offsetLeft
      : cards[0].offsetWidth + 24;

    const visible = Math.max(
      1,
      Math.floor(el.clientWidth / step)
    );

    el.scrollBy({
      left: direction * step * visible,
      behavior: "smooth",
    });
  };

  // ==========================================
  // KEYBOARD NAVIGATION
  // ==========================================

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollByCards(1);
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollByCards(-1);
    }
  };

  // ==========================================
  // DUPLICATE FOR INFINITE LOOP
  // ==========================================

  const sliderItems = [
    ...BENEFITS,
    ...BENEFITS,
  ];

  return (
    <section
      ref={wrapRef}
      className={`tb ${isVisible ? "tb--in" : ""}`}
    >
      {/* HEADER */}

      <div className="tb__head">
        <h2 className="tb__title">
          {TITLE}
        </h2>

        <span className="tb__underline" />
      </div>

      {/* INFINITE SLIDER */}

      <div className="tb__slider">

        {/* PREVIOUS */}

        <button
          type="button"
          className="tb__arrow tb__arrow--prev"
          onClick={() => scrollByCards(-1)}
          aria-label="Previous benefits"
        >
          <Chevron dir="left" />
        </button>

        {/* TRACK */}

        <div
          ref={trackRef}
          className="tb__track"
          tabIndex={0}
          role="list"
          aria-label="Business benefits"
          onKeyDown={onKeyDown}
        >
          {sliderItems.map((item, index) => (
            <article
              key={`${item.title}-${index}`}
              role="listitem"
              aria-hidden={
                index >= BENEFITS.length
              }
              className={`tb__card ${
                isVisible ? "visible" : ""
              }`}
              style={{
                transitionDelay:
                  isVisible &&
                  index < BENEFITS.length
                    ? `${index * 100}ms`
                    : "0ms",
              }}
            >
              {/* NUMBER */}

              <span
                className="tb__num"
                aria-hidden="true"
              >
                {String(
                  (index % BENEFITS.length) + 1
                ).padStart(2, "0")}
              </span>

              {/* ICON */}

              <div className="tb__icon">
                <i
                  className={`bi ${item.icon}`}
                  aria-hidden="true"
                />
              </div>

              {/* TITLE */}

              <h3 className="tb__card-title">
                {item.title}
              </h3>

              {/* DESCRIPTION */}

              <p>{item.desc}</p>
            </article>
          ))}
        </div>

        {/* NEXT */}

        <button
          type="button"
          className="tb__arrow tb__arrow--next"
          onClick={() => scrollByCards(1)}
          aria-label="Next benefits"
        >
          <Chevron dir="right" />
        </button>
      </div>

      {/* PROGRESS AND COUNTER */}

      {/* <div className="tb__meta">

        <span className="tb__count">
          <b>
            {String(current).padStart(2, "0")}
          </b>
          {" / "}
          {String(BENEFITS.length).padStart(2, "0")}
        </span>

        <span
          className="tb__progress"
          aria-hidden="true"
        >
          <i
            style={{
              transform: `scaleX(${Math.max(
                0.02,
                progress
              )})`,
            }}
          />
        </span>

      </div> */}
    </section>
  );
};

export default TPMS_17;
